import { ChangeDetectionStrategy, Component, Inject, Optional, forwardRef } from '@angular/core';
import { IProductConfigurableSelection, IProductDataV2 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Events } from '@df/core/service/event';
import { IAddToBasketData } from '@df/session/api/basket/basket';
import { BasketService, IPriceInfo } from '@df/session/api/basket/basket.service';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import intersection from 'lodash-es/intersection';
import { LocalProduct } from 'projects/app-default/src/app/catalog/model/product/product.decorator';
import { ModalLayoutRightComponent, NModalLayoutRight } from 'projects/app-default/src/app/ui/modal/modal-layout-right.component';
import { NOutfitBuilder } from '../../../../outfit-builder/outfit-builder.namespace';
import { NBuilderConfig } from '../../../builder.config';
import { NBuilderProductsSetup } from '../../builder-products-setup.config';
import { BuilderAddToBasketComponent } from '../builder-add-to-basket.component';
import { BuilderProductConfigurableSelectionComponent } from './product/builder-product-configurable-selection.component';

export namespace NBuilderConfigurableSelectionModal {
    /**
     * Modal identifier
     */
    export const id = 'builder-configurable-selection-modal';
    /**
     * Datapasset to modal
     */
    export interface IModalData {
        storageData: NOutfitBuilder.IStorageData;
        basketRequest: IAddToBasketData[];
        order: string[] | undefined;
    }
}
@DfModal({
    id: NBuilderConfigurableSelectionModal.id,
    name: 'Outfit Product Preview Popup',
    position: 'right',
    reopenPolicy: NDfModal.ReopenPolicy.first,
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: { S: '100vw', M: '100vw', L: '480px', X: '480px' }[sizeName],
        minWidth: { S: '100vw', M: '100vw' }[sizeName],
        height: '100%'
    }),
    sizeRestriction: sizeName => ({ S: ['S', 'M'], M: ['S', 'M'], L: ['L', 'X'], X: ['L', 'X'] })[sizeName]
})
@Component({
    selector: 'builder-configurable-selection-modal',
    templateUrl: './builder-configurable-selection-modal.html',
    standalone: true,
    imports: [
        forwardRef(() => BuilderAddToBasketComponent),
        BuilderProductConfigurableSelectionComponent,
        ModalLayoutRightComponent,
        PriceComponent,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuilderConfigurableSelectionModalComponent extends UiComponent implements NDfModal.IModalComponent {
    /**
     * Modal option config
     */
    modalConfig!: NDfModal.IModalConfig;

    protected selectedProducts: ({ product?: LocalProduct; selectionId: number } & NBuilderProductsSetup.ISelection)[] = [];

    constructor(
        private events: Events,
        private basketService: BasketService,
        private prouctResource: ProductResource,
        @Optional() protected modalRef: ModalRef<any>,
        @Optional() @Inject(MODAL_DATA) public data: NBuilderConfigurableSelectionModal.IModalData
    ) {
        super();
    }

    override ngOnInit(): void {
        this.updateSelectedProducts();
        super.ngOnInit();
    }

    private updateSelectedProducts(): void {
        const sectionIds = this.data.order
            ? intersection(this.data.order, Object.keys(this.data.storageData.selection!))
            : Object.keys(this.data.storageData.selection!);
        this.selectedProducts = sectionIds.map(selectionId => {
            const selection = this.data.storageData.selection![selectionId];
            return {
                selectionId,
                product: selection.productId ? (this.prouctResource.getExisting(selection.productId) as LocalProduct) : undefined,
                ...selection
            };
        });
    }
    /**
     * Close modal
     */
    protected close(): void {
        this.modalRef.close();
    }

    protected onSelectionChange(
        selectionId: number,
        originalProduct: LocalProduct,
        configurableSelection: IProductConfigurableSelection
    ): void {
        const matchedProduct = originalProduct.matchChild(configurableSelection) as LocalProduct<IProductDataV2>;
        this.data.storageData.selection![selectionId].matchedProductId = matchedProduct?.id;
        this.data.storageData.selection![selectionId].configurableSelection = configurableSelection;
        this.events.broadcast(NBuilderConfig.updateStorageEvent);
        this.updateBasketRequest();
    }

    protected updateBasketRequest(): void {
        this.data.basketRequest =
            this.basketService.normalizeAddToBasketData(
                (!!this.data.storageData.selection &&
                    Object.values(this.data.storageData.selection)?.map(option => ({
                        // if simple product isnt selected use configurable to calc price
                        id: option?.matchedProductId || option?.productId,
                        qty: 1
                    }))) ||
                    []
            ) || [];
        this.changeDetectorRef.detectChanges();
    }

    protected removeProduct(selectionId: number): void {
        if (this.data.storageData.selection?.[selectionId]) {
            delete this.data.storageData.selection[selectionId];
            this.events.broadcast(NBuilderConfig.updateStorageEvent);
            this.updateSelectedProducts();
            this.updateBasketRequest();
        }
    }

    get canAddToBasket(): boolean {
        return (
            !!this.data.storageData.selection &&
            Object.values(this.data.storageData.selection).every(
                option => !!option.matchedProductId && this.basketService.isSalable(this.data.basketRequest)
            )
        );
    }

    get totals(): IPriceInfo {
        return this.basketService.calcTotalPrices(this.data.basketRequest);
    }
}
